import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileDown, Check, CalendarCheck } from "lucide-react";
import { openBooking } from "../lib/booking";
import WhatsAppIcon from "./WhatsAppIcon";
import { waLink } from "../lib/whatsapp";
import { getLang } from "../lib/i18n";
import { trackEvent } from "../lib/track";
import { useBotGuard } from "../lib/botguard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    exitTitle: "Before you go —",
    exitGold: "take the launch pack.",
    exitBody: "Get the free Brokerage Launch Checklist — the six stages of a launch, from structure and licensing to payments, banking and go-live. We'll email it to you.",
    placeholder: "Your email",
    get: "Email me the checklist",
    or: "or talk to us now",
    wa: "Ask a question",
    thanks: "Sent — check your inbox (and spam folder) for the checklist.",
    invalid: "Enter a valid email",
    barBook: "Book $250 call",
    barAsk: "Ask a question",
    waMsg: "Hi Ah-Jin Group — I have a question about launching.",
  },
  es: {
    exitTitle: "Antes de irte —",
    exitGold: "llévate el paquete de lanzamiento.",
    exitBody: "Recibe gratis la Checklist de Lanzamiento — las seis etapas de un lanzamiento, desde estructura y licencias hasta pagos, banca y salida en vivo. Te la enviamos por email.",
    placeholder: "Tu email",
    get: "Enviarme la checklist",
    or: "o habla con nosotros ahora",
    wa: "Hacer una pregunta",
    thanks: "Enviada — revisa tu bandeja de entrada (y spam).",
    invalid: "Ingresa un email válido",
    barBook: "Reservar $250",
    barAsk: "Hacer una pregunta",
    waMsg: "Hola Ah-Jin Group — tengo una pregunta sobre mi lanzamiento.",
  },
};

const CaptureDialog = ({ open, onClose, t }) => {
  const bot = useBotGuard("lead");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErr(t.invalid); return; }
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { email, source: "exit-intent", lang: getLang() === "es" ? "es" : "en", ...bot.fields() });
      setSent(true);
      localStorage.setItem("ahjin_lead_done", "1");
      trackEvent("signup");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Please try again.");
      bot.reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-4"
          style={{ background: "rgba(3,6,16,0.72)", backdropFilter: "blur(6px)" }}
          onClick={onClose}
          data-testid="exit-popup-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-2xl border border-[#E5C56F]/25 p-6 sm:p-8"
            style={{ background: "linear-gradient(160deg, #0b1226 0%, #0a0716 100%)", boxShadow: "0 40px 120px -30px rgba(0,0,0,0.9)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="exit-popup"
          >
            <button
              onClick={onClose}
              data-testid="exit-popup-close"
              className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-200 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="w-11 h-11 rounded-xl border border-[#E5C56F]/30 bg-[#E5C56F]/10 flex items-center justify-center text-[#E5C56F]">
              <FileDown size={20} strokeWidth={1.5} />
            </div>
            <h3 className="font-display mt-4 text-2xl sm:text-3xl text-zinc-100 leading-tight">
              {t.exitTitle} <em className="not-italic gold-text">{t.exitGold}</em>
            </h3>
            <p className="mt-3 text-[13px] text-zinc-400 leading-relaxed">{t.exitBody}</p>

            {sent ? (
              <div className="mt-5 flex items-start gap-2 text-sm text-emerald-300" data-testid="exit-popup-success">
                <Check size={15} className="mt-0.5 shrink-0" /> {t.thanks}
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2.5">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.placeholder}
                  data-testid="exit-email-input"
                  className="flex-1 rounded-full bg-white/[0.04] border border-white/15 px-4 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-[#E5C56F]/50"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="exit-submit-btn"
                  className="rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1a1405] hover:brightness-110 transition-all disabled:opacity-60"
                >
                  {busy ? "…" : t.get}
                </button>
              </form>
            )}
            {!sent && <div className="mt-3">{bot.guard}</div>}
            {err && <p className="mt-2 text-xs text-red-400" data-testid="exit-error">{err}</p>}

            <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-3">
              <span className="text-[11px] text-zinc-500">{t.or}</span>
              <a
                href={waLink(t.waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="exit-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#4be284] hover:bg-[#25D366]/20 transition-colors"
              >
                <WhatsAppIcon size={13} /> {t.wa}
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const EngagementLayer = () => {
  const location = useLocation();
  const [popupOpen, setPopupOpen] = useState(false);
  const t = COPY[getLang() === "es" ? "es" : "en"];
  const onAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (onAdmin) return;
    const shown = () => sessionStorage.getItem("ahjin_exit_shown") || localStorage.getItem("ahjin_lead_done");
    const trigger = () => {
      if (shown()) return;
      sessionStorage.setItem("ahjin_exit_shown", "1");
      setPopupOpen(true);
    };
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    document.addEventListener("mouseout", onMouseOut);
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const timer = isTouch ? setTimeout(trigger, 45000) : null;
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      if (timer) clearTimeout(timer);
    };
  }, [onAdmin]);

  if (onAdmin) return null;

  return (
    <>
      {/* Sticky mobile CTA bar */}
      <div
        data-testid="mobile-cta-bar"
        className="md:hidden fixed bottom-0 inset-x-0 z-[75] flex gap-2 px-3 py-2.5 border-t border-white/10"
        style={{ background: "rgba(5,8,20,0.94)", backdropFilter: "blur(14px)", paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
      >
        <button
          type="button"
          onClick={() => openBooking("mobile-bar")}
          data-testid="mobile-cta-book"
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#1a1405] bg-gradient-to-r from-[#E5C56F] to-[#C9A64B]"
        >
          <CalendarCheck size={14} /> {t.barBook}
        </button>
        <a
          href={waLink(t.waMsg)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mobile-cta-whatsapp"
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white"
          style={{ background: "linear-gradient(135deg, #29d16a, #128C4A)" }}
        >
          <WhatsAppIcon size={15} /> {t.barAsk}
        </a>
      </div>

      <CaptureDialog open={popupOpen} onClose={() => setPopupOpen(false)} t={t} />
    </>
  );
};

export default EngagementLayer;
